// IND-033: vista «Desperdicio» dentro del Panel de planta. Consumo real del mes
// por grupo y desperdicio en kg, con las mismas fórmulas que la hoja DESPERDICIO
// del libro de recuento. El botón de acceso va en la barra del Panel.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';

import { getWasteControl, getWasteEpdmLines, importWasteHistory, saveWasteMonth } from '../services/industrialCostService';
import type { WasteControlMonth, WasteControlView, WasteEpdmLinesView } from '../services/industrialCostService';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';
import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import {
  defaultWasteYear,
  epdmLinesCheck,
  groupPct,
  kgDraft,
  MONTH_NAMES,
  MONTH_SHORT,
  parseKg,
  pendingText,
  wasteMiniBars,
  wasteNotices,
  wasteTone,
} from '../utils/wasteControl';
import SortableHeaderCell, { TEN_ROWS_MAX_HEIGHT } from '../components/SortableHeaderCell';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';
import IndustrialCostWasteCostSection from './IndustrialCostWasteCostSection';
import IndustrialCostWasteEpdmLinesSection from './IndustrialCostWasteEpdmLinesSection';
import IndustrialCostWasteNoticesDialog from './IndustrialCostWasteNoticesDialog';

const MUTED = '#94a3b8';
const HIGH = '#fb923c';
const LOW = '#38bdf8';
const BAR_DEFAULT = '#64748b';
const BAR_CLOSED = '#38bdf8';
const PANEL = {
  bgcolor: '#151f30',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 3,
  boxShadow: 'none',
};
const kg = esNumberFormat({ maximumFractionDigits: 0 });
const pctFormat = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtKg(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : kg.format(value);
}

function fmtPct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${pctFormat.format(value)} %`;
}

function fmtEur(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${kg.format(value)} €`;
}

function fmtPrice(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${pctFormat.format(value)} €/kg`;
}

function fmtPoints(value: number | null): string {
  if (value === null) return '—';
  const text = pctFormat.format(Math.abs(value));
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${text} pt`;
}

function errorText(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (error.response?.status === 403) return 'No tienes permiso para esta acción.';
  }
  return fallback;
}

export const WASTE_QUERY_KEY = ['industrial-cost-material-stock-projection', 'waste'];

export default function IndustrialCostWasteView({ onBack }: { onBack: () => void }) {
  const initialYear = defaultWasteYear();
  const [year, setYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const canImport = useCanManageImports();

  const query = useQuery({
    // Con el prefijo de la proyección: las cargas que la invalidan, también.
    queryKey: [...WASTE_QUERY_KEY, year],
    queryFn: () => getWasteControl(year),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const view = query.data;
  // IND-038: aparte, para que los metros por línea no retrasen la vista.
  const linesQuery = useQuery({
    queryKey: [...WASTE_QUERY_KEY, 'epdm-lines', year],
    queryFn: () => getWasteEpdmLines(year),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const notices = useMemo(() => (linesQuery.data ? wasteNotices(linesQuery.data) : []), [linesQuery.data]);

  useEffect(() => {
    setSelectedMonth(null);
    setOpenGroup(null);
  }, [year]);

  const today = new Date();
  const lastMonth = year === today.getFullYear() ? today.getMonth() + 1 : 12;
  const registerMonth = selectedMonth ?? view?.pending?.month ?? view?.default_month ?? lastMonth;
  const detailMonth = selectedMonth ?? view?.closed_month ?? view?.default_month ?? lastMonth;
  const reload = () => queryClient.invalidateQueries({ queryKey: WASTE_QUERY_KEY });
  const importMutation = useMutation({ mutationFn: importWasteHistory, onSuccess: () => void reload() });

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) importMutation.mutate(file);
  };

  const openDetail = (group: string) => {
    setSelectedMonth(view?.closed_month ?? null);
    setOpenGroup(group);
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pending = view ? pendingText(view) : null;

  return (
    <Stack spacing={1.5}>
      <Box>
        <Button startIcon={<ArrowBackRounded />} onClick={onBack} sx={{ textTransform: 'none', color: MUTED, px: 0 }}>
          Volver al Panel de planta
        </Button>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: 22 }}>Desperdicio</Typography>
          <Typography sx={{ color: MUTED, fontSize: 13 }}>
            Cuánto perdemos produciendo: consumo real del mes por grupo y desperdicio en kg, con las mismas fórmulas que el libro de recuento.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {notices.length ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<WarningAmberRounded />}
              onClick={() => setNoticesOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 800, color: '#fde68a', borderColor: 'rgba(251,191,36,0.5)', bgcolor: 'rgba(251,191,36,0.1)' }}
            >
              {`${notices.length} ${notices.length === 1 ? 'aviso' : 'avisos'}`}
            </Button>
          ) : null}
          <TextField select size="small" value={year} onChange={(event) => setYear(Number(event.target.value))} sx={{ minWidth: 90 }}>
            {[...new Set([today.getFullYear(), initialYear, today.getFullYear() - 1])].map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </TextField>
          <input ref={fileInput} type="file" accept=".xlsx" hidden onChange={handleFile} />
          <Button
            size="small"
            variant="outlined"
            disabled={importMutation.isPending || !canImport}
            title={canImport ? undefined : IMPORT_PERMISSION_DENIED_TEXT}
            onClick={() => fileInput.current?.click()}
            sx={{ textTransform: 'none' }}
          >
            {importMutation.isPending ? 'Importando…' : 'Importar histórico del libro'}
          </Button>
          <Button size="small" variant="contained" disabled={!view} onClick={() => setRegisterOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {`Registrar desperdicio de ${MONTH_NAMES[registerMonth - 1].toLowerCase()}`}
          </Button>
        </Stack>
      </Stack>

      {importMutation.isError ? (
        <Alert severity="error">{errorText(importMutation.error, 'No se pudo importar el histórico.')}</Alert>
      ) : null}
      {importMutation.isSuccess ? (
        <Alert severity="success">{`Importados ${importMutation.data.imported_months.length} meses de ${importMutation.data.year}.`}</Alert>
      ) : null}
      {importMutation.data?.months_without_prices.length ? (
        <Alert severity="warning">
          {`Sin hoja STP-MP legible para ${importMutation.data.months_without_prices.map((month) => MONTH_NAMES[month - 1].toLowerCase()).join(', ')}: esos meses conservan el precio que tuvieran.`}
        </Alert>
      ) : null}
      {pending ? <Alert severity="warning">{pending}</Alert> : null}

      {query.isError ? (
        <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !view ? (
        <DetailLoading height={260} />
      ) : (
        <>
          <YearBlock view={view} />
          <ClosedMonthCards view={view} onOpen={openDetail} />
          <EvolutionTable view={view} lastMonth={lastMonth} year={year} />
          <IndustrialCostWasteEpdmLinesSection query={linesQuery} />
          <Box ref={detailRef}>
            <MonthDetail
              view={view}
              lastMonth={lastMonth}
              month={detailMonth}
              onMonth={(value) => {
                setSelectedMonth(value);
                setOpenGroup(null);
              }}
              openGroup={openGroup}
              onToggleGroup={(group) => setOpenGroup(openGroup === group ? null : group)}
            />
          </Box>
          <IndustrialCostWasteCostSection year={year} />
        </>
      )}

      {view ? (
        <RegisterDialog
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          year={year}
          monthNumber={registerMonth}
          month={view.months.find((item) => item.month === registerMonth) ?? null}
          groups={view.waste_groups}
          labels={Object.fromEntries(view.groups.map((item) => [item.group, item.label]))}
          linesView={linesQuery.data}
          linesFetching={linesQuery.isFetching}
          onSaved={() => void reload()}
        />
      ) : null}
      <IndustrialCostWasteNoticesDialog
        open={noticesOpen}
        onClose={() => setNoticesOpen(false)}
        year={year}
        notices={notices}
        onFix={(month) => {
          setSelectedMonth(month);
          setOpenGroup(null);
          setNoticesOpen(false);
          setRegisterOpen(true);
        }}
      />
    </Stack>
  );
}

function MiniBars({ view, group, average }: { view: WasteControlView; group: string; average: number | null }) {
  const { bars, averageHeight } = wasteMiniBars(view, group, average);
  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ position: 'relative', height: 70, display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
        {bars.map((bar) => (
          <MuiTooltip key={bar.month} title={`${bar.name}: ${fmtPct(bar.pct)}`} placement="top">
            {/* En sx, un número ≤ 1 es un porcentaje: la altura mínima va en px. */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                height: `${bar.height}%`,
                minHeight: '2px',
                bgcolor: bar.closed ? BAR_CLOSED : BAR_DEFAULT,
                borderRadius: '2px 2px 0 0',
              }}
            />
          </MuiTooltip>
        ))}
        {averageHeight !== null ? (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${averageHeight}%`,
              borderTop: '1px dashed #f8fafc',
              opacity: 0.6,
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: '3px', mt: '2px' }}>
        {bars.map((bar) => (
          <Typography
            key={bar.month}
            sx={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 9.5, lineHeight: 1.2, color: bar.closed ? BAR_CLOSED : MUTED, fontWeight: bar.closed ? 900 : 600 }}
          >
            {bar.initial}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function YearBlock({ view }: { view: WasteControlView }) {
  const totals = view.year_totals;
  const items = [
    { label: 'Kg perdidos', value: `${fmtKg(totals.waste_kg)} kg` },
    { label: '€ perdidos', value: `${totals.price_partial ? '≈ ' : ''}${fmtEur(totals.waste_eur)}` },
    { label: '% sobre el consumo', value: fmtPct(totals.pct) },
  ];
  return (
    <Paper sx={{ ...PANEL, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{`Año ${view.year}`}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 11 }}>
          {`la media de la tabla es la del Excel${totals.price_partial ? ' · precio parcial' : ''}`}
        </Typography>
      </Stack>
      <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        {items.map((entry) => (
          <Box key={entry.label}>
            <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 800 }}>{entry.label}</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 950, color: '#f8fafc' }}>{entry.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function ClosedMonthCards({ view, onOpen }: { view: WasteControlView; onOpen: (group: string) => void }) {
  const closed = view.closed_month;
  if (closed === null) {
    return <Typography sx={{ color: MUTED, fontSize: 13 }}>{`Sin meses cerrados en ${view.year}. Importa el histórico del libro.`}</Typography>;
  }
  const month = view.months.find((item) => item.month === closed);
  const previous = [...view.months].reverse().find((item) => item.month < closed && item.has_consumption);
  return (
    <Box>
      <Typography sx={{ fontWeight: 900, fontSize: 13.5, mb: 0.75 }}>{`Último mes cerrado: ${MONTH_NAMES[closed - 1].toLowerCase()}`}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
        {view.waste_groups.map((group) => {
          const row = month?.groups.find((item) => item.group === group);
          const pct = row?.pct ?? null;
          const average = view.averages[group] ?? null;
          const previousPct = groupPct(previous, group);
          const alert = wasteTone(pct, average) === 'high';
          return (
            <Paper
              key={group}
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(group)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onOpen(group);
              }}
              sx={{ p: 1.15, borderRadius: 2, cursor: 'pointer', minWidth: 0, borderColor: alert ? HIGH : undefined }}
            >
              <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 800 }}>{row?.label ?? group}</Typography>
              <Typography sx={{ mt: 0.3, fontSize: 20, fontWeight: 950, color: alert ? HIGH : '#f8fafc' }}>{fmtPct(pct)}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                {`${fmtPoints(pct !== null && previousPct !== null ? pct - previousPct : null)} mes anterior`}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                {`${fmtPoints(pct !== null && average !== null ? pct - average : null)} media · ${fmtKg(row?.waste_kg)} kg`}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                {`≈ ${fmtEur(row?.waste_eur)} este mes${row?.price_partial ? ' · precio parcial' : ''}`}
              </Typography>
              <MiniBars view={view} group={group} average={average} />
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

type Metric = 'pct' | 'waste' | 'consumption' | 'eur';

function metricValue(item: WasteControlMonth | undefined, group: string, metric: Metric): number | null {
  const row = item?.groups.find((entry) => entry.group === group);
  if (!row) return null;
  if (metric === 'eur') return row.waste_eur;
  return metric === 'pct' ? row.pct : metric === 'waste' ? row.waste_kg : row.consumption_kg;
}

function EvolutionTable({ view, lastMonth, year }: { view: WasteControlView; lastMonth: number; year: number }) {
  const [metric, setMetric] = useState<Metric>('pct');
  const sort = useTableSort<string>(null, 'desc');
  const monthNumbers = useMemo(() => Array.from({ length: 12 }, (_, index) => index + 1), []);
  const byMonth = useMemo(() => new Map(view.months.map((item) => [item.month, item])), [view]);
  const now = new Date();
  const isPast = (month: number) =>
    year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);

  const averageOf = (group: string): number | null => {
    if (metric === 'pct') return view.averages[group] ?? null;
    const values = view.months
      .map((item) => metricValue(item, group, metric))
      .filter((value): value is number => value !== null);
    return values.length ? values.reduce((total, value) => total + value, 0) / values.length : null;
  };

  const value = (group: { group: string; label: string }): SortValue =>
    sort.key === 'group'
      ? group.label
      : sort.key === 'media'
        ? averageOf(group.group)
        : metricValue(byMonth.get(Number(sort.key)), group.group, metric);
  const rows = sort.key ? sortRows(view.groups, value, sort.direction) : view.groups;
  const format = metric === 'pct' ? fmtPct : metric === 'eur' ? fmtEur : fmtKg;

  return (
    <Paper sx={{ ...PANEL, p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{`Evolución de ${view.year}`}</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_, next: Metric | null) => next && setMetric(next)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1, py: 0.25 } }}
        >
          <ToggleButton value="pct">%</ToggleButton>
          <ToggleButton value="waste">Desperdicio kg</ToggleButton>
          <ToggleButton value="consumption">Consumo kg</ToggleButton>
          <ToggleButton value="eur">€</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
          <TableHead>
            <TableRow>
              <SortableHeaderCell label="Grupo" columnKey="group" sort={sort} firstDirection="asc" sx={{ bgcolor: '#151f30' }} />
              {monthNumbers.map((month) => (
                <SortableHeaderCell key={month} label={MONTH_SHORT[month - 1]} columnKey={String(month)} sort={sort} align="right" sx={{ bgcolor: '#151f30' }} />
              ))}
              <SortableHeaderCell label="Media" columnKey="media" sort={sort} align="right" sx={{ bgcolor: '#151f30' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((group) => {
              const average = averageOf(group.group);
              return (
                <TableRow key={group.group}>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{group.label}</TableCell>
                  {monthNumbers.map((month) => {
                    const cell = metricValue(byMonth.get(month), group.group, metric);
                    const tone = metric === 'pct' ? wasteTone(cell, average) : null;
                    const item = byMonth.get(month);
                    const row = item?.groups.find((entry) => entry.group === group.group);
                    const pendingCell =
                      cell === null &&
                      month <= lastMonth &&
                      isPast(month) &&
                      group.group !== 'PVC_NR' &&
                      (!item || row?.waste_kg === null);
                    return (
                      <TableCell
                        key={month}
                        align="right"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                          color: tone === 'high' ? HIGH : tone === 'low' ? LOW : pendingCell ? MUTED : undefined,
                        }}
                      >
                        {pendingCell ? 'pend.' : format(cell)}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {format(average)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

type DetailSortKey = 'group' | 'consumption' | 'waste' | 'pct' | 'price' | 'eur';
const DETAIL_COLUMNS: Array<{ key: DetailSortKey; label: string; value: (row: WasteControlMonth['groups'][number]) => SortValue }> = [
  { key: 'group', label: 'Grupo', value: (row) => row.label },
  { key: 'consumption', label: 'Consumo kg', value: (row) => row.consumption_kg },
  { key: 'waste', label: 'Desperdicio kg', value: (row) => row.waste_kg },
  { key: 'pct', label: '%', value: (row) => row.pct },
  { key: 'price', label: 'Precio', value: (row) => row.price_eur_kg },
  { key: 'eur', label: '€', value: (row) => row.waste_eur },
];

type LineSortKey = 'material' | 'consumption' | 'share' | 'counted';
const LINE_COLUMNS: Array<{ key: LineSortKey; label: string; value: (line: WasteControlMonth['materials'][string][number]) => SortValue }> = [
  { key: 'material', label: 'Material', value: (line) => line.material_code },
  { key: 'consumption', label: 'Consumo', value: (line) => line.consumption_kg },
  { key: 'share', label: '% destino', value: (line) => line.share },
  { key: 'counted', label: 'Cuenta kg', value: (line) => line.counted_kg },
];

function consumptionOrigin(month: WasteControlMonth | undefined): string {
  if (month?.origin === 'excel') return 'hoja DESPERDICIO';
  if (month?.origin === 'proa') return `columna CONSUMO de STP-MP-${MONTH_NAMES[month.month - 1].toUpperCase()}`;
  return '—';
}

function MonthDetail({
  view,
  lastMonth,
  month,
  onMonth,
  openGroup,
  onToggleGroup,
}: {
  view: WasteControlView;
  lastMonth: number;
  month: number;
  onMonth: (month: number) => void;
  openGroup: string | null;
  onToggleGroup: (group: string) => void;
}) {
  const item = view.months.find((entry) => entry.month === month);
  const sort = useTableSort<DetailSortKey>(null, 'desc');
  const column = DETAIL_COLUMNS.find((entry) => entry.key === sort.key);
  const groupRows = item ? (column ? sortRows(item.groups, column.value, sort.direction) : item.groups) : [];
  const hasMaterials = Boolean(item) && Object.values(item?.materials ?? {}).some((lines) => lines.length > 0);
  return (
    <Paper sx={{ ...PANEL, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>Detalle del mes</Typography>
        <TextField select size="small" value={month} onChange={(event) => onMonth(Number(event.target.value))} sx={{ minWidth: 140 }}>
          {Array.from({ length: lastMonth }, (_, index) => index + 1).map((value) => (
            <MenuItem key={value} value={value}>{MONTH_NAMES[value - 1]}</MenuItem>
          ))}
        </TextField>
        {item && item.missing_waste.length > 0 && item.has_consumption ? (
          <Typography sx={{ color: '#fbbf24', fontSize: 11 }}>{`falta el desperdicio de ${item.missing_waste.join(', ')}`}</Typography>
        ) : null}
      </Stack>
      {!item ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>
          {`Sin datos de ${MONTH_NAMES[month - 1].toLowerCase()}. El consumo sale al cargar el libro con el recuento de cierre; el desperdicio se puede registrar ya.`}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {DETAIL_COLUMNS.map((entry) => (
                  <SortableHeaderCell
                    key={entry.key}
                    label={entry.label}
                    columnKey={entry.key}
                    sort={sort}
                    align={entry.key === 'group' ? 'left' : 'right'}
                    firstDirection={entry.key === 'group' ? 'asc' : 'desc'}
                  />
                ))}
                <TableCell align="right">Frente a la media</TableCell>
                <TableCell>Consumo de</TableCell>
                <TableCell>Registrado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupRows.map((row) => {
                const lines = item.materials[row.group];
                const expandable = Boolean(lines && lines.length > 0);
                const average = view.averages[row.group] ?? null;
                return (
                  <Fragment key={row.group}>
                    <TableRow
                      hover={expandable}
                      selected={openGroup === row.group}
                      onClick={expandable ? () => onToggleGroup(row.group) : undefined}
                      sx={{ cursor: expandable ? 'pointer' : undefined }}
                    >
                      <TableCell sx={{ fontWeight: 800 }}>{`${expandable ? (openGroup === row.group ? '▾ ' : '▸ ') : ''}${row.label}`}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtKg(row.consumption_kg)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtKg(row.waste_kg)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(row.pct)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {fmtPrice(row.price_eur_kg)}
                        {row.price_partial ? <Typography component="span" sx={{ display: 'block', color: '#fbbf24', fontSize: 10 }}>precio parcial</Typography> : null}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtEur(row.waste_eur)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtPoints(row.pct !== null && average !== null ? row.pct - average : null)}
                      </TableCell>
                      <TableCell sx={{ color: MUTED, fontSize: 11 }}>{consumptionOrigin(item)}</TableCell>
                      <TableCell sx={{ color: MUTED, fontSize: 11 }}>
                        {row.waste_source === 'proa'
                          ? `PROA${row.updated_by_label ? ` · ${row.updated_by_label}` : ''}`
                          : row.waste_source === 'excel'
                            ? 'Excel'
                            : ''}
                      </TableCell>
                    </TableRow>
                    {openGroup === row.group && lines ? (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0.5 }}>
                          <MaterialLines lines={lines} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {item?.has_consumption && !hasMaterials ? (
        <Typography sx={{ color: MUTED, fontSize: 11, mt: 0.5 }}>
          {`Para ver los materiales de cada grupo, carga la hoja STP-MP-${MONTH_NAMES[month - 1].toUpperCase()} del libro de recuento.`}
        </Typography>
      ) : null}
      {item && item.origin === 'proa' && item.warnings.length > 0 ? (
        <Typography sx={{ color: '#fbbf24', fontSize: 11, mt: 0.5 }}>
          {`${item.warnings.length} avisos en el cálculo: ${[...new Set(item.warnings.map((entry) => entry.material_code).filter(Boolean))].slice(0, 6).join(', ')}`}
        </Typography>
      ) : null}
    </Paper>
  );
}

function MaterialLines({ lines }: { lines: WasteControlMonth['materials'][string] }) {
  const total = lines.reduce((sum, line) => sum + line.counted_kg, 0);
  const sort = useTableSort<LineSortKey>('counted', 'desc');
  const column = LINE_COLUMNS.find((entry) => entry.key === sort.key) ?? LINE_COLUMNS[3];
  const sorted = sortRows(lines, column.value, sort.direction);
  return (
    <TableContainer sx={{ maxHeight: TEN_ROWS_MAX_HEIGHT }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <SortableHeaderCell label="Material" columnKey="material" sort={sort} firstDirection="asc" sx={{ bgcolor: '#151f30' }} />
            <TableCell sx={{ bgcolor: '#151f30' }}>Proveedor</TableCell>
            <SortableHeaderCell label="Consumo" columnKey="consumption" sort={sort} align="right" sx={{ bgcolor: '#151f30' }} />
            <SortableHeaderCell label="% destino" columnKey="share" sort={sort} align="right" sx={{ bgcolor: '#151f30' }} />
            <SortableHeaderCell label="Cuenta kg" columnKey="counted" sort={sort} align="right" sx={{ bgcolor: '#151f30' }} />
            <TableCell align="right" sx={{ bgcolor: '#151f30' }}>Peso</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((line) => (
            <TableRow key={`${line.material_id}-${line.supplier ?? ''}`}>
              <TableCell>
                <Typography sx={{ fontWeight: 800, fontSize: 12 }}>{line.material_code}</Typography>
                {line.material_name && line.material_name !== line.material_code ? (
                  <Typography sx={{ color: MUTED, fontSize: 10.5 }}>{line.material_name}</Typography>
                ) : null}
              </TableCell>
              <TableCell sx={{ color: MUTED, fontSize: 11 }}>{line.supplier ?? ''}</TableCell>
              <TableCell align="right">{fmtKg(line.consumption_kg)}</TableCell>
              <TableCell align="right">{`${kg.format(line.share * 100)} %`}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 900 }}>{fmtKg(line.counted_kg)}</TableCell>
              <TableCell align="right">{total ? fmtPct((line.counted_kg / total) * 100) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RegisterDialog({
  open,
  onClose,
  year,
  monthNumber,
  month,
  groups,
  labels,
  linesView,
  linesFetching,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  year: number;
  monthNumber: number;
  month: WasteControlMonth | null;
  groups: string[];
  labels: Record<string, string>;
  linesView: WasteEpdmLinesView | undefined;
  linesFetching: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [lineDraft, setLineDraft] = useState<Record<string, string>>({});
  const lines = linesView?.lines ?? [];
  const linesTouched = useRef(false);
  useEffect(() => {
    if (!open) return;
    setDraft(
      Object.fromEntries(
        groups.map((group) => {
          const row = month?.groups.find((item) => item.group === group);
          // Un 0 supuesto (mes importado) no se precarga: guardarlo lo volvería dato de PROA.
          const value = row?.waste_assumed ? null : row?.waste_kg;
          return [group, kgDraft(value)];
        }),
      ),
    );
    // Solo al abrir: no pisa lo que se escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      linesTouched.current = false;
      return;
    }
    if (linesFetching || linesTouched.current) return;
    const saved = linesView?.months.find((item) => item.month === monthNumber);
    setLineDraft(
      Object.fromEntries(
        (linesView?.lines ?? []).map((line) => [line.key, kgDraft(saved?.values[line.key])]),
      ),
    );
    // Al abrir y al terminar de leer las líneas: no pisa lo que se escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linesFetching]);

  const parsed = Object.fromEntries(groups.map((group) => [group, parseKg(draft[group] ?? '')])) as Record<string, number | null>;
  const invalid = groups.filter((group) => {
    const value = parsed[group];
    return value !== null && (!Number.isFinite(value) || value < 0);
  });
  const parsedLines = Object.fromEntries(lines.map((line) => [line.key, parseKg(lineDraft[line.key] ?? '')])) as Record<string, number | null>;
  const invalidLines = lines.filter((line) => {
    const value = parsedLines[line.key];
    return value !== null && (!Number.isFinite(value) || value < 0);
  });
  const check = epdmLinesCheck(Object.values(parsedLines), parsed.EPDM ?? null);

  const mutation = useMutation({
    mutationFn: () => saveWasteMonth(year, monthNumber, parsed, lines.length ? parsedLines : undefined),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{`Desperdicio de ${MONTH_NAMES[monthNumber - 1].toLowerCase()} de ${year}`}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: MUTED, fontSize: 12, mb: 1.25 }}>En kg. Vacío = sin dato (no es 0).</Typography>
        <Stack spacing={1.25}>
          {groups.map((group) => (
            <TextField
              key={group}
              size="small"
              label={group === 'EPDM' ? 'EPDM (total)' : labels[group] ?? group}
              value={draft[group] ?? ''}
              onChange={(event) => setDraft({ ...draft, [group]: event.target.value })}
              error={invalid.includes(group)}
              helperText={invalid.includes(group) ? 'Pon un número mayor o igual que 0' : ' '}
            />
          ))}
        </Stack>
        {lines.length ? (
          <Box sx={{ mt: 0.5, p: 1.25, borderRadius: 2, border: '1px solid rgba(148,163,184,0.16)' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 1 }}>
              EPDM por línea{' '}
              <Typography component="span" sx={{ color: MUTED, fontSize: 13 }}>(opcional)</Typography>
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 1 }}>
              {lines.map((line) => (
                <TextField
                  key={line.key}
                  size="small"
                  label={line.name}
                  value={lineDraft[line.key] ?? ''}
                  disabled={linesFetching}
                  onChange={(event) => {
                    linesTouched.current = true;
                    setLineDraft({ ...lineDraft, [line.key]: event.target.value });
                  }}
                  error={invalidLines.includes(line)}
                />
              ))}
            </Box>
            {invalidLines.length ? (
              <Typography sx={{ color: 'error.main', fontSize: 12, mt: 1 }}>Pon un número mayor o igual que 0</Typography>
            ) : check ? (
              <Typography sx={{ color: check.ok ? '#86efac' : '#fde68a', fontSize: 12, mt: 1 }}>{check.text}</Typography>
            ) : null}
          </Box>
        ) : null}
        {mutation.isError ? (
          <Alert severity="error" sx={{ mt: 1 }}>{errorText(mutation.error, 'No se pudo guardar el desperdicio.')}</Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={invalid.length > 0 || invalidLines.length > 0 || linesFetching || mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
